import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { UserContext } from '@incentivegames/ig-frontend-common/lib/contexts/userContext';
import { useEntryStore } from '../../store/entry/entryStore';
import { Prize, Entry } from '../../interfaces/predictors';
import { communicationUtilities } from '@incentivegames/ig-frontend-common/lib/utilities/communicationUtilities';
import './EntryStateText.scss';
import DisplayCurrencyOrPrizeSuffix from '../DisplayPrizeCurrencyOrSuffix/DisplayPrizeCurrencyOrSuffix';
import ActionButton from '../ActionButton/ActionButton';
import { getPrizeCodeClassModifier, getPrizeSuffixTranslationKey } from '../PrizePool/PrizePool.functions';

interface GetTextProps {
  settled: boolean;
  t: TFunction;
  currency: string;
  prize?: Prize;
}

export const calculatePrizeValue = ({ entry }: { entry: Entry }): number => {
  const { prizes, returns } = entry;
  const prize = prizes?.[0];

  if (prize?.prizeCode === 'freespin') {
    return prize.prizeMultiplier;
  }

  if (prize?.prizeAmount) {
    return prize.prizeAmount;
  }

  return returns;
};

const getText = ({ t, currency, settled, prize }: GetTextProps) => {
  const lost = prize === undefined && settled;
  const won = prize && settled;

  const prizeCodeClassModifier = getPrizeCodeClassModifier({ prizeCode: prize?.prizeCode });
  const suffixTranslationKey = getPrizeSuffixTranslationKey({ prizeCode: prize?.prizeCode });

  if (lost) return <div className={'entry-state-text__lose'}>{t('butter-luck-next-time')}</div>;
  if (won && prize.prizeCode !== 'freespin')
    return (
      <>
        <div className={'entry-state-text__win'}>{t('congratulations-you-won')}</div>
        <div
          className={
            prizeCodeClassModifier
              ? `entry-state-text__prize entry-state-text__prize${prizeCodeClassModifier}`
              : 'entry-state-text__prize'
          }
        >
          <DisplayCurrencyOrPrizeSuffix
            value={prize.prizeAmount}
            currency={currency}
            prizeCode={prize.prizeCode}
            prizeMultiplier={prize.prizeMultiplier}
            suffix={suffixTranslationKey}
          />
        </div>

        {prize.prizeCode === 'freespin' && (
          <ActionButton
            onClick={() => communicationUtilities.sendPostMessage.launchGame()}
            translationKey={'play-here'}
          />
        )}
      </>
    );
  //if we haven't won or lost then this component is being shown on the my-selections screen
  return (
    <>
      <div className={'entry-state-text__good-luck'}>{t('selections-entered-good-luck')}</div>
      <div className={'entry-state-text__edit-selection'}>{t('you-can-edit-selections-until-first-race')}</div>
    </>
  );
};

const shouldDisplayText = (prize: Prize | undefined) => {
  if (!prize) return true;
  if (prize.prizeCode !== 'freespin') return true;
  return prize.prizeSettled;
};

const EntryStateText = () => {
  const { t } = useTranslation();

  const entry = useEntryStore((s) => s.entry);
  const { user } = useContext(UserContext);

  if (!entry || !shouldDisplayText(entry.prizes?.[0])) return null;
  const { settled } = entry;

  return (
    <div className={`entry-state-text ${settled ? 'settled' : ''} `}>
      {getText({
        t,
        prize: entry.prizes?.[0],
        settled: settled ?? false,
        currency: user.currency ?? 'USD',
      })}
    </div>
  );
};

export default EntryStateText;
